import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@ui/card.tsx';
import { HistoryIcon, StarIcon, Coins, TrendingUp, Search, Package } from 'lucide-react';
import {
  type FormattedDataForRendering,
  BarChartBloc
} from '@r/components/kpi/points/bar-chart-bloc.tsx';
import { BarChartSkeleton } from '@r/components/kpi/skeleton/bar-chart-skeleton.tsx';
import { useTranslation } from 'react-i18next';
import pointsTranslation from '@r/translations/points-translation.ts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { RadialChart } from '@client/components/objective/radial-chart.tsx';
import { ObjectiveItem } from '@client/components/objective/objective-item.tsx';
import { getBarFillFromValue } from '@r/utils/chart-utils.ts';
import { useContext, useMemo, useState } from 'react';
import { useUser } from '@r/api/auth.tsx';
import { DateRangePicker } from '@ui/date-range-picker.tsx';
import { WorkspaceContext } from '@r/context/workspace-context.ts';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { ProgressMultiObjectives } from '@client/components/objective/progress-multi-objectives.tsx';
import { PointsHistoryAccordion } from '@client/components/performances/points-history-accordion.tsx';
import { PageContainer } from '@client/components/layout/page-container.tsx';
import Marquee from '@ui/marquee.tsx';
import { PerformancesTargetSkeleton } from '@r/components/kpi/skeleton/performances-target-skeleton.tsx';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import {
  isTargetLogicWorkspace,
  isLinearLogicWorkspace,
  isStepLogicWorkspace
} from '@r/utils/workspace-utils.ts';
import { LogicLogicReadModelType } from '@r/api/generated/client.schemas.ts';
import { useMeInsights } from '@r/api/generated/client.ts';
import {
  SearchableSelectTagInput,
  type Option
} from '@client/components/performances/searchable-select-tag-input.tsx';
import type {
  LogicTypeTargetStatusMeReadModel,
  PerformanceInsightUserMeReadModel
} from '@r/api/generated/client.schemas.ts';
import { useAllReferences } from '@r/hooks/use-all-references.ts';

function formatMonth(month: number, year: number): string {
  const name = format(new Date(year, month - 1), 'MMMM', { locale: fr });
  return `${name.charAt(0).toUpperCase() + name.slice(1)} ${year}`;
}

function formatDate(isoDate: string): string {
  return format(new Date(isoDate), 'dd MMM yyyy', { locale: fr });
}

const REFERENCES_ORDER_PARAMS = {
  'order[brand]': 'asc',
  'order[productLine]': 'asc',
  'order[name]': 'asc'
} as const;

const EmptyState = ({ label }: { label: string }) => (
  <div className="flex items-center justify-center h-24 text-sm text-muted-foreground">{label}</div>
);

const PerformancePage = () => {
  const { t } = useTranslation();

  const user = useUser().data;
  const workspaceContext = useContext(WorkspaceContext);
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const { workspace, isWorkspacePending, isWorkspaceError } = workspaceContext;

  const company = user?.visibleWorkspaces?.[0]?.company ?? null;
  const targetStatuses = user?.visibleWorkspaces?.[0]?.company?.targetStatuses;
  const currencySymbol = user?.visibleWorkspaces?.[0]?.company?.country?.currencySymbol;

  const isTarget = isTargetLogicWorkspace(workspace);
  const isStepOrLinear = isStepLogicWorkspace(workspace) || isLinearLogicWorkspace(workspace);
  const isTargetPointLogic =
    workspace?.currentLogicWorkspace?.logic.type === LogicLogicReadModelType.TARGET_POINT;

  const isTargetStatusVisible =
    (!isStepLogicWorkspace(workspace) && !isLinearLogicWorkspace(workspace)) ||
    (targetStatuses != null && targetStatuses.length > 0);

  const cardBorder = isTarget ? 'border' : 'border-0';

  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({});
  const [selectedReferences, setSelectedReferences] = useState<Option[]>([]);

  const referenceUniqueCodes = selectedReferences.map((r) => String(r.id));

  const baseInsightsParams = {
    ...(dateRange.from ? { from: dateRange.from } : {}),
    ...(dateRange.to ? { to: dateRange.to } : {})
  };

  const insightsParams = {
    ...baseInsightsParams,
    ...(referenceUniqueCodes.length > 0 ? { 'referenceUniqueCode[]': referenceUniqueCodes } : {})
  };

  const userInsights = useMeInsights(insightsParams, {
    query: { queryKey: ['user_insights_list', insightsParams] }
  });

  const referencesQuery = useAllReferences(REFERENCES_ORDER_PARAMS);

  const referenceOptions: Option[] = useMemo(
    () =>
      (referencesQuery.data ?? []).map((reference) => ({
        id: reference.uniqueCode,
        name: reference.name
      })),
    [referencesQuery.data]
  );

  const formattedPointsPerMonth: FormattedDataForRendering[] = userInsights.isSuccess
    ? userInsights.data.insightPoints.totalPointsPerMonth.map((item) => ({
        date: formatMonth(item.month, item.year),
        data: Number(item.value),
        fill: getBarFillFromValue(Number(item.value))
      }))
    : [];

  const totalPeriodPoints: number = userInsights.isSuccess
    ? userInsights.data.insightPoints.totalPointsPerMonth.reduce(
        (acc, item) => acc + Number(item.value),
        0
      )
    : 0;

  const euroPerformances: PerformanceInsightUserMeReadModel[] = userInsights.isSuccess
    ? (userInsights.data.insightPerformance?.performancesTargetHistory ?? []).filter(
        (p) => p.referenceQuantity == null
      )
    : [];

  const hasEuroData = euroPerformances.length > 0;

  const quantityPerformances: PerformanceInsightUserMeReadModel[] = userInsights.isSuccess
    ? (userInsights.data.insightPerformance?.performancesTargetHistory ?? []).filter(
        (p) => p.referenceQuantity != null
      )
    : [];

  const hasQuantityData = quantityPerformances.length > 0;

  const monthlyQuantityData: FormattedDataForRendering[] = (() => {
    if (!userInsights.isSuccess) return [];

    const byMonth = new Map<string, { month: number; year: number; total: number }>();

    for (const item of userInsights.data.insightPoints.totalPointsPerMonth) {
      byMonth.set(`${item.year}-${item.month}`, { month: item.month, year: item.year, total: 0 });
    }

    for (const item of userInsights.data.insightPerformance?.performancesTargetHistory ?? []) {
      const d = new Date(item.realizedAt);
      const month = d.getMonth() + 1;
      const year = d.getFullYear();
      const key = `${year}-${month}`;
      if (!byMonth.has(key)) byMonth.set(key, { month, year, total: 0 });
      if (item.referenceQuantity != null) {
        const monthData = byMonth.get(key);
        if (monthData) {
          monthData.total += item.referenceQuantity;
        }
      }
    }

    return Array.from(byMonth.values())
      .sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month))
      .map((item) => ({
        date: formatMonth(item.month, item.year),
        data: item.total,
        fill: getBarFillFromValue(item.total)
      }));
  })();

  if (isWorkspacePending || isWorkspaceError) {
    return <PerformancesTargetSkeleton />;
  }

  return (
    <PageContainer className="pt-10">
      {/* ── Filtres ── */}
      <div>
        <Card>
          <CardContent className="flex flex-wrap gap-5 pt-5">
            <div className="flex flex-col space-y-1">
              <div className="text-sm font-bold">{t('admin.kpi.filter.date.title')}</div>
              <DateRangePicker
                isActive={Boolean(dateRange.from && dateRange.to)}
                placeholder={t('date.range.choose.period')}
                onUpdate={({ range }) => {
                  if (!range.from || !range.to) {
                    setDateRange({});
                    return;
                  }
                  setDateRange({
                    from: format(range.from, 'yyyy-MM-dd'),
                    to: format(range.to, 'yyyy-MM-dd')
                  });
                }}
              />
            </div>
            {(referencesQuery.isLoading || referenceOptions.length > 0) && (
              <div className="flex flex-col space-y-1">
                <div className="text-sm font-bold">{t('kpi.filter.reference.title')}</div>
                <SearchableSelectTagInput
                  value={selectedReferences}
                  onChange={setSelectedReferences}
                  options={referenceOptions}
                  loading={referencesQuery.isLoading}
                  getOverflowMessage={(count) => t('kpi.filter.reference.search.refine', { count })}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Points section ── */}
      {(isStepOrLinear || isTarget) && (
        <section id="section-progression" className="flex flex-col py-7 grow">
          <h1 className="mb-10 font-bold text-2xl text-foreground md:text-3xl lg:mb-16 lg:text-[2.75rem] lg:leading-[3.5rem]">
            {isTarget ? t('performance.target.commercial.points.title') : t('performance.title')}
          </h1>

          {
            <div className="flex flex-wrap space-x-0 space-y-7 lg:flex-nowrap lg:space-x-7 lg:space-y-0">
              {/* Chart */}
              <Card className={`flex flex-col justify-between w-full ${cardBorder} lg:w-6/12`}>
                {userInsights.isLoading && <BarChartSkeleton />}
                {userInsights.isSuccess && (
                  <>
                    <CardHeader className="space-y-0">
                      <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                        {isTarget ? (
                          <div className="flex gap-2 items-center pb-4 font-bold">
                            <Coins /> {t('performance.target.loyalty.points')}
                          </div>
                        ) : (
                          <>
                            <StarIcon />
                            <div className="text-xl font-bold">
                              {t('performance.histogram.title')}
                            </div>
                          </>
                        )}
                      </CardTitle>
                      <span>
                        <div className="mb-2 text-3xl font-bold">
                          {pointsTranslation(t, totalPeriodPoints, true)}
                        </div>
                        {!isTarget && (
                          <div className="text-sm font-light text-muted-foreground">
                            {t('performance.histogram.info')}
                          </div>
                        )}
                      </span>
                    </CardHeader>
                    <CardContent className="mt-20 h-[15rem] flex-grow font-regular">
                      {formattedPointsPerMonth.length > 0 ? (
                        <BarChartBloc
                          formattedDataPerMonth={formattedPointsPerMonth}
                          dataType="point"
                        />
                      ) : (
                        <EmptyState label={t('performance.no.data')} />
                      )}
                    </CardContent>
                  </>
                )}
              </Card>

              {/* Points history */}
              <Card className={`flex flex-col w-full ${cardBorder} lg:w-6/12`}>
                <CardHeader>
                  <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                    <HistoryIcon />
                    <div className="text-xl font-bold">
                      {t('performance.history.title', { pointsName: t('point.name_other') })}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 max-h-[400px] overflow-y-auto">
                  {userInsights.isLoading && (
                    <div className="space-y-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
                      ))}
                    </div>
                  )}
                  {userInsights.isSuccess &&
                    userInsights.data.insightPoints.pointsHistory.length === 0 && (
                      <EmptyState label={t('performance.no.data')} />
                    )}
                  {userInsights.isSuccess &&
                    userInsights.data.insightPoints.pointsHistory.length > 0 && (
                      <PointsHistoryAccordion
                        pointsHistory={userInsights.data.insightPoints.pointsHistory}
                        totalPointsPerMonth={userInsights.data.insightPoints.totalPointsPerMonth}
                      />
                    )}
                </CardContent>
              </Card>
            </div>
          }
        </section>
      )}

      {/* ── Quantité section ── */}
      {isTarget && (userInsights.isLoading || hasQuantityData) && (
        <section id="section-perf-quantity" className="flex flex-col py-7 grow">
          <div className="flex flex-wrap space-x-0 space-y-7 lg:flex-nowrap lg:space-x-7 lg:space-y-0">
            {/* Monthly quantity chart */}
            <Card className="flex flex-col justify-between w-full border lg:w-6/12">
              {userInsights.isLoading && <BarChartSkeleton />}
              {userInsights.isSuccess && (
                <>
                  <CardHeader className="space-y-0">
                    <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                      <Package />
                      <div className="text-xl font-bold">
                        {t('performance.quantity.histogram.title')}
                      </div>
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="mt-20 h-[15rem] flex-grow font-regular">
                    <BarChartBloc formattedDataPerMonth={monthlyQuantityData} dataType="quantity" />
                  </CardContent>
                </>
              )}
            </Card>

            {/* Quantity history */}
            <Card className="flex flex-col w-full border lg:w-6/12">
              <CardHeader>
                <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                  <HistoryIcon />
                  <div className="text-xl font-bold">{t('performance.quantity.history.title')}</div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
                {userInsights.isLoading && (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />
                    ))}
                  </div>
                )}
                {userInsights.isSuccess && quantityPerformances.length === 0 && (
                  <EmptyState label={t('performance.no.data')} />
                )}
                {userInsights.isSuccess &&
                  quantityPerformances.map((perf, i) => (
                    <div key={i} className="flex justify-between px-5 py-3 rounded-lg bg-muted">
                      <div className="flex flex-col gap-0.5">
                        {perf.logicTypeTarget?.reference && (
                          <span className="font-bold text-sm text-muted-foreground">
                            {perf.logicTypeTarget.reference}
                          </span>
                        )}
                        {perf.label && (
                          <span className="text-sm text-muted-foreground">{perf.label}</span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDate(perf.realizedAt)}
                        </span>
                      </div>
                      <div className="flex flex-col items-end gap-0.5 shrink-0">
                        {perf.referenceQuantity != null && (
                          <span className="font-bold text-primary">
                            <span className="text-xs font-normal text-muted-foreground">
                              {t('performance.quantity.count.label')}{' '}
                            </span>
                            {perf.referenceQuantity}
                          </span>
                        )}
                        {perf.amount != null && perf.amount > 0 && (
                          <span className="text-sm text-muted-foreground">
                            {isTargetPointLogic
                              ? pointsTranslation(t, perf.amount, true)
                              : `${perf.amount} ${currencySymbol ?? '€'}`}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* ── Objectif section ── */}
      {isTarget &&
        isTargetStatusVisible &&
        (userInsights.isLoading ||
          hasEuroData ||
          (company?.targetStatuses?.length ?? 0) > 0 ||
          (userInsights.data?.insightPerformance?.targetsAmountPerMonth ?? []).some((item) =>
            item.targets.some((t) => t.totalAmount > 0)
          )) && (
          <section id="section-progression-target" className="flex flex-col gap-10 py-7 grow">
            <h2 className="font-bold text-2xl text-foreground md:text-3xl lg:text-[2.75rem] lg:leading-[3.5rem]">
              {t('performance.target.title')}
            </h2>

            {userInsights.isLoading && (
              <div className="flex flex-wrap space-x-0 space-y-7 lg:flex-nowrap lg:space-x-7 lg:space-y-0">
                <div className="w-full lg:w-2/3 h-64 rounded-xl bg-muted animate-pulse" />
                <div className="w-full lg:w-1/3 h-64 rounded-xl bg-muted animate-pulse" />
              </div>
            )}
            {userInsights.data &&
              ((userInsights.data.insightPerformance?.targetsAmountPerMonth ?? []).some((item) =>
                item.targets.some((t) => t.totalAmount > 0)
              ) ||
                hasEuroData) && (
                <div className="flex flex-wrap space-x-0 space-y-7 lg:flex-nowrap lg:space-x-7 lg:space-y-0">
                  {(userInsights.data.insightPerformance?.targetsAmountPerMonth ?? []).some(
                    (item) => item.targets.some((t) => t.totalAmount > 0)
                  ) && (
                    <ProgressMultiObjectives
                      userInsights={userInsights.data}
                      className={hasEuroData ? 'lg:w-2/3' : 'w-full'}
                    />
                  )}
                  {hasEuroData && (
                    <Card
                      className={`flex flex-col border ${(userInsights.data.insightPerformance?.targetsAmountPerMonth ?? []).some((item) => item.targets.some((t) => t.totalAmount > 0)) ? 'w-full lg:w-1/3' : 'w-full'}`}
                    >
                      <CardHeader>
                        <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                          <HistoryIcon />
                          <div className="text-xl font-bold">{t('performance.target.history')}</div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
                        {euroPerformances.map((perf, i) => (
                          <div
                            key={i}
                            className="flex justify-between px-5 py-3 rounded-lg bg-muted"
                          >
                            <div className="flex flex-col gap-0.5">
                              {perf.logicTypeTarget?.reference && (
                                <span className="font-bold">{perf.logicTypeTarget.reference}</span>
                              )}
                              {perf.label && <span>{perf.label}</span>}
                              <span className="text-xs text-muted-foreground">
                                {formatDate(perf.realizedAt)}
                              </span>
                            </div>
                            {perf.amount != null && perf.amount > 0 && (
                              <div className="text-primary shrink-0">
                                <FormattedNumber
                                  value={perf.amount}
                                  style={isTargetPointLogic ? 'decimal' : undefined}
                                  suffix={
                                    isTargetPointLogic
                                      ? ` ${pointsTranslation(t, perf.amount, false)}`
                                      : undefined
                                  }
                                />
                              </div>
                            )}
                            {perf.amount != null && perf.amount < 0 && (
                              <div className="text-destructive shrink-0">
                                <FormattedNumber
                                  value={perf.amount}
                                  style={isTargetPointLogic ? 'decimal' : undefined}
                                  suffix={
                                    isTargetPointLogic
                                      ? ` ${pointsTranslation(t, perf.amount, false)}`
                                      : undefined
                                  }
                                />
                              </div>
                            )}
                            {(perf.amount == null || perf.amount === 0) && (
                              <div className="text-muted-foreground shrink-0">
                                <FormattedNumber
                                  value={0}
                                  style={isTargetPointLogic ? 'decimal' : undefined}
                                  suffix={
                                    isTargetPointLogic
                                      ? ` ${pointsTranslation(t, 0, false)}`
                                      : undefined
                                  }
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

            {userInsights.isLoading && <div className="h-48 rounded-xl bg-muted animate-pulse" />}
            {userInsights.data && (company?.targetStatuses?.length ?? 0) > 0 && (
              <div className="flex flex-wrap space-x-0 space-y-7 lg:flex-nowrap lg:space-x-7 lg:space-y-0">
                <Card
                  className={`flex flex-col border ${(userInsights.data.insightPerformance?.targetsAmountPerMonth ?? []).some((item) => item.targets.some((t) => t.totalAmount > 0)) ? 'w-full lg:w-2/3' : 'w-full'}`}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                      <Search />
                      <div className="text-xl font-bold">{t('performance.target.view')}</div>
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-3">
                    {company?.targetStatuses?.map((status) => (
                      <ObjectiveItem
                        key={status.id}
                        targetStatus={status as LogicTypeTargetStatusMeReadModel}
                        isTargetPointLogic={isTargetPointLogic}
                      />
                    ))}
                  </CardContent>
                </Card>
                {(userInsights.data.insightPerformance?.targetsAmountPerMonth ?? []).some((item) =>
                  item.targets.some((t) => t.totalAmount > 0)
                ) && (
                  <Card className="flex flex-col w-full border lg:w-1/3">
                    <CardHeader>
                      <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                        <TrendingUp />
                        <div className="text-xl font-bold">{t('performance.target.overview')}</div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <RadialChart
                        userInsights={userInsights.data}
                        isTargetPointLogic={isTargetPointLogic}
                      />
                    </CardContent>
                    <CardFooter>
                      <p className="text-sm text-center text-muted-foreground">
                        {t('performance.target.overview.footer')}
                      </p>
                    </CardFooter>
                  </Card>
                )}
              </div>
            )}
          </section>
        )}

      <Marquee text={workspaceConfigContext?.groupedConfigs?.core?.title ?? null} />
    </PageContainer>
  );
};

export default PerformancePage;
